import { useAppDispatch, useAppSelector } from '@hooks/reduxHooks';
import { baseErrorHandler } from '@queries/baseErrorHandler';
import { setFlats } from '@reducers/CreateContractSlice';
import { setFlats as setFlatsV2 } from '@reducers/CreateContractSliceV2';
import { $api } from '@service/RequestService';
import { timeStampToDate } from '@utils/index';
import { FlatMore } from '@utils/models/FlatMore';
import { Pagination } from '@utils/models/Pagination';
import { useInfiniteQuery, useQuery } from 'react-query';
import { NumberParam, useQueryParam } from 'use-query-params';

import { endPoints } from '../utils/constants/endPoints';
import { queryKeys } from '../utils/constants/queryKeys';
import { FlatEventGroupType, FlatEventStatisticType } from '../utils/models/house-history-model';

export function useFlatMore(idsList: string[]) {
  const dispatch = useAppDispatch();
  const [offerId] = useQueryParam('offerId', NumberParam);
  const v2 = useAppSelector(state => state.createContractV2.v2);

  let ids = `?flats=${idsList[0]}`;

  if (idsList.length > 1) {
    for (let i = 1; i < idsList.length; i++) {
      ids += `&flats=${idsList[i]}`;
    }
  }

  return useQuery<FlatMore[]>(
    [queryKeys.FLAT_MORE_LIST, ids, offerId],
    async () => {
      const res = await $api.get(`${endPoints.FLAT_MORE}${ids}${offerId ? `&offerId=${offerId}` : ''}`);

      return res?.data?.data;
    },
    {
      onSuccess: val => {
        if (v2) {
          dispatch(setFlatsV2(val));
        } else {
          dispatch(setFlats(val));
        }
      },
      retry: false,
      onError: baseErrorHandler,
      refetchOnWindowFocus: false
    }
  );
}

export function useFlatEventStatistics() {
  const initial: FlatEventStatisticType = {
    createdAt: '',
    createdBy: ''
  };

  const { id } = useAppSelector(({ houseHistorySlice }) => houseHistorySlice);

  const { data = initial, ...arg } = useQuery<FlatEventStatisticType>(
    ['flat-event-statistics', id],
    async () => {
      const { data } = await $api.get(`/v1/flat-event/stats/${id}`);

      const flatData: FlatEventStatisticType = data?.data;

      return {
        ...flatData,
        createdAt: flatData?.createdAt
          ? timeStampToDate({ date: flatData?.createdAt, timeStamp: flatData?.createdTimestamp })
          : '',
        bookedAt: flatData?.bookedAt
          ? timeStampToDate({ date: flatData?.bookedAt, timeStamp: flatData?.bookedTimestamp })
          : undefined,
        contractedAt: flatData?.contractedAt
          ? timeStampToDate({ date: flatData?.contractedAt, timeStamp: flatData?.contractedTimestamp })
          : undefined
      };
    },
    {
      enabled: !!id
    }
  );

  return { ...data, ...arg };
}

// INFINITE QUERY NOT WORKING REAL TIME
export function useFlatEvents() {
  const { id, dynamicNumber } = useAppSelector(({ houseHistorySlice }) => houseHistorySlice);

  const initial: { pages: Pagination<FlatEventGroupType>[] } = {
    pages: [
      {
        data: [],
        totalPages: 0,
        currentPage: 1,
        totalElements: 0
      }
    ]
  };

  const { data = initial, ...arg } = useInfiniteQuery(
    ['flat-events', id, dynamicNumber],
    async ({ pageParam = 1 }) => {
      const res = await $api.get(`/v1/flat-event/${id}?page=${pageParam}&size=20`);

      const data = res?.data?.data;

      return data
        ? {
            ...data,
            data: Object.fromEntries(
              Object.entries((data?.data || {}) as FlatEventGroupType).map(([key, arr]) => [
                key,
                arr?.map(item => ({
                  ...item,
                  date: timeStampToDate({ date: item?.date, timeStamp: item?.timestamp })
                }))
              ])
            )
          }
        : data;
    },
    {
      // enabled not working !!!
      enabled: !!id,
      staleTime: 3,
      retry: true,
      refetchOnWindowFocus: true,
      getNextPageParam: (lastPage, allPages) => {
        if (allPages?.length < lastPage?.totalPages) {
          return allPages.length + 1;
        }

        return undefined;
      }
    }
  );

  return { ...data, ...arg };
}
